import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { AuthBackgroundLayout } from '../layout/AuthBackgroundLayout';
import { PATHS } from '../constants/Paths.constants';
import { showToast } from '../store/slices/toastSlice';

const EMAIL_STORAGE_KEY = 'pendingVerificationEmail';

export function CheckYourEmail() {
  const reduxEmail = useSelector((state: RootState) => state.auth.user?.email);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState<string>('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  // Load email from Redux or localStorage
  useEffect(() => {
    if (reduxEmail) {
      setEmail(reduxEmail);
      // Save to localStorage for refresh case
      localStorage.setItem(EMAIL_STORAGE_KEY, reduxEmail);
    } else {
      // Try to load from localStorage (refresh case)
      const savedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
      if (savedEmail) {
        setEmail(savedEmail);
      } else {
        // No email available, redirect to registration
        navigate(PATHS.REGISTRATION);
      }
    }
  }, [reduxEmail, navigate]);

  // Clear localStorage when email is verified (cleanup)
  useEffect(() => {
    return () => {
      // Clear on unmount only if navigating away (not refresh)
      const navigationEntries = performance.getEntriesByType(
        'navigation'
      ) as PerformanceNavigationTiming[];
      const isNavigatingAway =
        navigationEntries.length === 0 ||
        navigationEntries[0].type !== 'reload';
      if (isNavigatingAway) {
        localStorage.removeItem(EMAIL_STORAGE_KEY);
      }
    };
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResend = async () => {
    if (countdown > 0 || !email) return;

    setIsResending(true);
    setResendSuccess(false);

    try {
      const response = await fetch('/api/v1/auth/email/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setResendSuccess(true);
        setCountdown(60);
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        const data = await response.json();
        dispatch(
          showToast({
            message:
              data.message || 'Failed to resend email. Please try again.',
            type: 'error'
          })
        );
      }
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      dispatch(
        showToast({
          message: 'Network error. Please check your connection and try again.',
          type: 'error'
        })
      );
    } finally {
      setIsResending(false);
    }
  };

  const getButtonText = () => {
    if (isResending) return 'Sending...';
    if (countdown > 0) return `Resend in ${countdown}s`;
    return 'Click to Resend';
  };

  return (
    <div className="h-screen flex items-center justify-center">
      <AuthBackgroundLayout>
        <p className="text-2xl text-purple-900 font-bold text-left self-stretch">
          Please Check Your Email
        </p>
        <p className="text-base font-normal text-gray-950 text-left self-stretch">
          We sent confirmation link to
          <br />
          <span className="font-semibold break-words">{email}</span>
        </p>

        {resendSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-md p-3 self-stretch">
            <p className="text-sm text-green-800">
              ✓ Verification email sent successfully!
            </p>
          </div>
        )}

        {resendError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 self-stretch">
            <p className="text-sm text-red-800">{resendError}</p>
          </div>
        )}

        <div className="flex flex-row items-center justify-between text-sm text-gray-950 self-stretch">
          <p>Didn't receive the email?</p>
          <button
            type="button"
            className={`text-sm bg-transparent border-none cursor-pointer p-0 font-medium ${
              countdown > 0 || isResending
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-purple-800 hover:text-blue-500'
            }`}
            onClick={handleResend}
            disabled={countdown > 0 || isResending}
          >
            {getButtonText()}
          </button>
        </div>
      </AuthBackgroundLayout>
    </div>
  );
}
